/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  CommandButton,
  MessageBar,
  MessageBarType,
  TextField,
  Stack,
} from '@fluentui/react';
import { Page } from '../Page';
import { CommandBar } from '../CommandBar';
import { Container } from '../Container';
import { Paper } from '../Paper';

import { useBreadcrumbs } from '../../providers/BreadcrumbsProvider';
import { useToast } from 'providers/ToastContextProvider';
import { ICourse } from 'types/ICourse';
import { useCreateCourse, useDeleteCourse, useCourseSingle, useUpdateCourse } from 'hooks/useCourses';

const getFormErrors = (form: ICourse): any => {
  const errors: any = {};

  if (!form.level1) {
    errors.level1 = 'Level 1 is required.';
  }
  if (!form.level2) {
    errors.level2 = 'Level 2 is required.';
  }
  if (!form.course) {
    errors.course = 'Course is required.';
  }
  if (!form.wiki_url) {
    errors.wiki_url = 'Wiki URL is required.';
  }

  return errors;
};

export const CourseFormPage: React.FC = () => {
  const history = useHistory();
  const { courseId } = useParams<any>();
  const { course } = useCourseSingle(courseId);
  const { setToastSuccessMessage } = useToast();

  const [data, setData] = useState(course);
  
  const { setBreadcrumbs } = useBreadcrumbs();
  const [errors, setErrors] = useState<any>({});
  const [generalError, setGeneralError] = useState('');

  const { create, isLoading: isCreating } = useCreateCourse({
    onSuccess: () => {
      history.push(`/managecourses`);
      setToastSuccessMessage('Course has been created.');
    },
    onError: () =>
      setGeneralError(
        'An error occurred while trying to create a new course.'
      ),
  });
  const { update, isLoading: isUpdating } = useUpdateCourse({
    onSuccess: () => {
      history.push(`/managecourses`);
      setToastSuccessMessage('Course has been updated.');
    },
    onError: () =>
      setGeneralError(
        'An error occurred while trying to update the course.'
      ),
  });
  const { delete: deleteSkill, isLoading: isDeleting } =
    useDeleteCourse({
      onSuccess: () => {
        history.push(`/managecourses`);
      },
      onError: () =>
        setGeneralError(
          'An error occurred while trying to delete the course.'
        ),
    });

  const isSubmitting = useMemo(
    () => isCreating || isUpdating || isDeleting,
    [isCreating, isUpdating, isDeleting]
  );

  useEffect(() => {
    setBreadcrumbs([
      {
        key: 'courses',
        text: 'Courses',
        onClick: () => history.push('/managecourses'),
      },
      ...(courseId
        ? [{ key: 'course', text: data?.course ?? 'Unknown' }]
        : [{ key: 'new', text: 'New' }]),
    ]);
  }, [history, data, courseId]);

  const onSubmit = async () => {
    const form = data ?? {};
    const errors = getFormErrors(form);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }
    setErrors({});
    const payload = form;
    if (!form.id) {
      create({ payload });
    } else {
      update({ courseId, payload  });
    }
  };

  const onDelete = useCallback(async () => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      deleteSkill(courseId);
    }
  }, [data]);

  return (
    <Page>
      <CommandBar>
        {data?.id && (
          <>
            <CommandButton
              iconProps={{ iconName: 'Undo' }}
              text="Discard Changes"
              onClick={() => history.push(`/managecourses`)}
              disabled={isSubmitting}
            />
            <CommandButton
              iconProps={{ iconName: 'Delete' }}
              text="Delete Course"
              onClick={onDelete}
              disabled={isSubmitting}
            />
            <CommandButton
              iconProps={{ iconName: 'Save' }}
              text="Update Course"
              onClick={onSubmit}
              disabled={isSubmitting}
            />
          </>
        )}
        {!data?.id && (
          <>
            <CommandButton
              iconProps={{ iconName: 'Cancel' }}
              text="Cancel"
              onClick={() => history.push(`/managecourses`)}
              disabled={isSubmitting}
            />
            <CommandButton
              iconProps={{ iconName: 'Save' }}
              text="Create Course"
              onClick={onSubmit}
              disabled={isSubmitting}
            />
          </>
        )}
      </CommandBar>
      <Container>
        <header>
          <h2>{data?.id ? 'Edit Course' : 'Create New Course'}</h2>
        </header>
        <Paper>
          <div className="da-form">
            {generalError && (
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
              >
                {generalError}
              </MessageBar>
            )}
            <Stack horizontal tokens={{ childrenGap: 10 }}>
              <Stack.Item grow>
                <TextField
                  label="Level 1"
                  value={data?.level1 ? data.level1 + '' : ''}
                  onChange={(_: any, level1: any) =>
                    setData((state: ICourse) => ({ ...state, level1 }))
                  }
                  errorMessage={errors.level1}
                  disabled={isSubmitting}
                />
              </Stack.Item>
              <Stack.Item grow>
                <TextField
                  label="Level 2"
                  value={data?.level2 ? data.level2 + '' : ''}
                  onChange={(_: any, level2: any) =>
                    setData((state: ICourse) => ({ ...state, level2 }))
                  }
                  errorMessage={errors.level2}
                  disabled={isSubmitting}
                />
              </Stack.Item>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 10 }}>
              <Stack.Item grow>
                <TextField
                  label="Course"
                  value={data?.course ? data.course + '' : ''}
                  onChange={(_: any, course: any) =>
                    setData((state: ICourse) => ({ ...state, course }))
                  }
                  errorMessage={errors.course}
                  disabled={isSubmitting}
                />
              </Stack.Item>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 10 }}>
              <Stack.Item grow>
                <TextField
                  label="Wiki URL"
                  value={data?.wiki_url ? data.wiki_url + '' : ''}
                  onChange={(_: any, wiki_url: any) =>
                    setData((state: ICourse) => ({ ...state, wiki_url }))
                  }
                  errorMessage={errors.wiki_url}
                  disabled={isSubmitting}
                />
              </Stack.Item>
            </Stack>
          </div>
        </Paper>
      </Container>
    </Page>
  );
};

export default CourseFormPage;
